import { observer } from 'mobx-react-lite'
import React, { type ChangeEvent, useRef } from 'react'
import classNames from 'classnames'
import {
  Button,
  Dropdown,
  DropdownCard,
  DropdownContent,
  EnumHelperText,
  HelperText,
} from 'shared/ui'
import { LINK_KNOWLEDGE_BASE_HELP_GUIDE } from 'shared/constants/links'
import { uiStore } from 'shared/store/uiStore'
import { makeTextFieldAutoFocus, makeTextFieldIcon, TextField } from 'shared/ui/TextField'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { KnowledgeBasePath } from 'entities/ExportRouters'
import { useKnowledgeBaseViewContext } from 'pages/settings/pages/knowledgeBase/context/knowledgeBaseViewContext'
import styles from './styles.module.scss'

export const KnowledgeBaseActions = observer(() => {
  const { attachmentStore } = useEntitiesContext()
  const {
    documentsStore: { isEmpty, onUploadDocuments, urlStore },
    isUploadDisabled,
  } = useKnowledgeBaseViewContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    url,
    setUrl,
    isUploading,
    handleImportUrl,
    isUploadViaUrlOpen,
    setIsUploadViaUrlOpen,
    errorMessages,
  } = urlStore

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  const handleCreateDocument = () => {
    uiStore.changeRouteViaVue({
      path: KnowledgeBasePath.createDocument,
    })
  }

  const onInputFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (files) {
      void onUploadDocuments(Array.from(files))
    }
  }

  return (
    <div
      className={classNames(styles.actionsContainer, {
        [styles.emptyView]: isEmpty,
      })}
    >
      {!isUploadViaUrlOpen && (
        <>
          <Button
            tag='a'
            text='Help guide'
            size='medium'
            contained='secondary'
            iconProps={{ icon: 'info' }}
            className={classNames(styles.helpButton, {
              [styles.emptyView]: isEmpty,
            })}
            href={LINK_KNOWLEDGE_BASE_HELP_GUIDE}
            target='_blank'
            rel='noreferrer'
          />
          <input
            type={'file'}
            className={styles.fileInput}
            ref={inputRef}
            accept={attachmentStore.extendedFilesAcceptString}
            disabled={isUploadDisabled}
            multiple
            onChange={onInputFileChange}
          />
          <Dropdown
            triggerComponent={() => (
              <Button text='Add knowledge' iconProps={{ icon: 'add' }} size='medium' />
            )}
            placement={'bottom-end'}
            customComponent={(onCloseMenu) => (
              <DropdownContent width={194}>
                <DropdownCard
                  item={{
                    id: 'upload',
                    label: 'Upload or drop',
                    iconL: 'upload',
                    ariaLabel: 'uploadSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    handleUploadClick()
                  }}
                />
                <DropdownCard
                  item={{
                    id: 'url',
                    label: 'Import web page',
                    iconL: 'globe',
                    ariaLabel: 'urlSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    setIsUploadViaUrlOpen(true)
                  }}
                />
                <DropdownCard
                  item={{
                    id: 'block',
                    label: 'Create document',
                    iconL: 'edit',
                    ariaLabel: 'createDocumentSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    handleCreateDocument()
                  }}
                />
              </DropdownContent>
            )}
          />
        </>
      )}

      {isUploadViaUrlOpen && (
        <div className={styles.uploadViaUrlContainer}>
          <TextField
            size='extraSmall'
            variant='outline'
            value={url}
            onChange={setUrl}
            InputProps={{ placeholder: 'https://website.com' }}
            disabled={isUploading}
            leftActions={[
              makeTextFieldIcon({
                icon: 'globe',
                color: 'var(--content-primary-tertiary)',
                fontSize: 16,
                margin: '0 0 0 8px',
              }),
            ]}
            rightActions={[
              () => (
                <Button
                  typeBtn='text'
                  size='small'
                  text='Cancel'
                  disabled={isUploading}
                  onClick={() => {
                    setUrl('')
                    setIsUploadViaUrlOpen(false)
                  }}
                />
              ),
              () => (
                <Button
                  size='small'
                  text='Import'
                  disabled={!url || isUploading}
                  onClick={handleImportUrl}
                />
              ),
            ]}
            mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
            className={classNames(styles.textInput, styles.uploadViaUrl)}
            error={errorMessages.url}
          />
          {errorMessages.url && (
            <HelperText
              paddingBlock={'3.5px'}
              icon={'alertTriangleSmall'}
              variant={EnumHelperText.Error}
              text={errorMessages.url}
            />
          )}
        </div>
      )}
    </div>
  )
})
